import { Alert, Box, Card, CardContent, Typography } from '@mui/material'
import UploadIcon from '@mui/icons-material/Upload'
import DownloadIcon from '@mui/icons-material/Download'
import { ChangeEvent, useCallback, useRef, useState } from 'react'
import { getChipData } from 'utils/api'
import { DeepcellPrimaryButton } from 'components/shared/DeepcellPrimaryButton'
import LinearProgressWithLabel from 'components/shared/LinearProgressWithLabel'

interface ExportProgress {
  current: number
  total: number
}

interface ChipError {
  barcode: string
  error: string
  id: string
}

interface ExportResult {
  successful: number
  failed: number
  errors: ChipError[]
  chips: string[]
}

/**
 * Parses a TSV file and extracts chip barcodes from the 'barcode' column
 * @param file The TSV file to parse
 * @returns Promise resolving to an array of chip barcodes
 */
const parseTsvFile = (file: File): Promise<string[]> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()

    reader.onload = () => {
      try {
        const content = reader.result as string
        const lines = content.split('\n').map(line => line.trim()).filter(line => line.length > 0)

        if (lines.length === 0) {
          reject(new Error('File is empty'))
          return
        }

        // Parse the header to find the barcode column
        const headers = lines[0].split('\t').map(h => h.trim())
        const barcodeIndex = headers.findIndex(h => h.toLowerCase() === 'barcode')

        if (barcodeIndex === -1) {
          reject(new Error(`No "barcode" column found in TSV file. Available columns: ${headers.join(', ')}`))
          return
        }

        // Extract barcodes from each row
        const barcodes: string[] = []
        for (let i = 1; i < lines.length; i += 1) {
          const row = lines[i].split('\t')
          if (row.length > barcodeIndex) {
            const barcode = row[barcodeIndex].trim()
            if (barcode.length > 0) {
              barcodes.push(barcode)
            }
          }
        }

        if (barcodes.length === 0) {
          reject(new Error('No valid barcodes found in the file'))
          return
        }

        resolve(barcodes)
      } catch (err) {
        reject(new Error(`Failed to parse TSV file: ${err instanceof Error ? err.message : 'Unknown error'}`))
      }
    }

    reader.onerror = () => {
      reject(new Error('Error reading file'))
    }

    reader.readAsText(file)
  })
}

/**
 * Downloads the export data as a JSON file
 * @param exportData The data to export
 */
const downloadJsonFile = (exportData: Record<string, unknown>): void => {
  const json = JSON.stringify(exportData, null, 2)
  const blob = new Blob([json], { type: 'application/json' })
  const url = URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.download = `chip-export-${new Date().toISOString().split('T')[0]}.json`
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
  URL.revokeObjectURL(url)
}

export const ChipExportTool = (): JSX.Element | null => {
  const uploadInputRef = useRef<HTMLInputElement>(null)
  const [exporting, setExporting] = useState<boolean>(false)
  const [progress, setProgress] = useState<ExportProgress | null>(null)
  const [result, setResult] = useState<ExportResult | null>(null)
  const [error, setError] = useState<string | null>(null)

  /**
   * Exports chip data by calling the API for each barcode
   * @param barcodes Array of chip barcodes to export
   */
  const exportChips = async (barcodes: string[]): Promise<void> => {
    const total = barcodes.length
    const errors: ChipError[] = []
    const chips: string[] = []

    setProgress({ current: 0, total })

    // Process chips sequentially to avoid overwhelming the API
    // eslint-disable-next-line no-plusplus
    for (let i = 0; i < barcodes.length; i++) {
      const barcode = barcodes[i]

      try {
        // eslint-disable-next-line no-await-in-loop
        const response = await getChipData(barcode)
        chips.push(response.chip)
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Unknown error'
        errors.push({
          barcode,
          error: errorMessage,
          id: `error-${i}`,
        })
      }

      setProgress({ current: i + 1, total })
    }

    setResult({
      successful: chips.length,
      failed: errors.length,
      errors,
      chips,
    })
  }

  const handleFileUpload = useCallback(async (e: ChangeEvent<HTMLInputElement>) => {
    // Reset state
    setError(null)
    setResult(null)
    setProgress(null)

    if (!e.target.files || e.target.files.length === 0) {
      return
    }

    const file = e.target.files[0]

    // Reset input so the same file can be uploaded again
    if (uploadInputRef.current) {
      uploadInputRef.current.value = ''
    }

    // Validate file extension
    const extension = file.name.split('.').pop()?.toLowerCase()
    if (extension !== 'tsv') {
      setError(`Invalid file type: .${extension}. Please upload a TSV file.`)
      return
    }

    setExporting(true)

    try {
      const barcodes = await parseTsvFile(file)
      await exportChips(barcodes)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to export chips')
    } finally {
      setExporting(false)
    }
  }, [])

  const handleDownload = useCallback(() => {
    if (!result || result.chips.length === 0) {
      return
    }

    const exportData = {
      exportDate: new Date().toISOString(),
      chipCount: result.chips.length,
      chips: result.chips,
    }

    downloadJsonFile(exportData)
  }, [result])

  return (
    <Card sx={{ maxWidth: 800, margin: '0 auto' }}>
      <CardContent>
        <Typography variant="h6" gutterBottom>
          Chip Export Tool
        </Typography>
        <Typography variant="body2" color="text.secondary" paragraph>
          Upload a TSV file containing chip barcodes to export chip data. The TSV file must have a
          &quot;barcode&quot; column.
        </Typography>

        <Box sx={{ mb: 3 }}>
          <DeepcellPrimaryButton
            contained
            startIcon={<UploadIcon />}
            onClick={() => uploadInputRef.current?.click()}
            disabled={exporting}
          >
            Upload TSV File
            <input
              ref={uploadInputRef}
              data-testid="chip-export-input"
              hidden
              type="file"
              accept=".tsv"
              onChange={handleFileUpload}
            />
          </DeepcellPrimaryButton>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {progress && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="body2" gutterBottom>
              Processing {progress.current}/{progress.total} chips...
            </Typography>
            <LinearProgressWithLabel
              value={(progress.current / progress.total) * 100}
            />
          </Box>
        )}

        {result && (
          <Box>
            <Alert severity={result.failed === 0 ? 'success' : 'warning'} sx={{ mb: 2 }}>
              Export complete: {result.successful} successful, {result.failed} failed
            </Alert>

            {result.chips.length > 0 && (
              <Box sx={{ mb: 2 }}>
                <DeepcellPrimaryButton
                  contained
                  startIcon={<DownloadIcon />}
                  onClick={handleDownload}
                >
                  Download Export ({result.chips.length} chips)
                </DeepcellPrimaryButton>
              </Box>
            )}

            {result.errors.length > 0 && (
              <Box>
                <Typography variant="subtitle2" gutterBottom>
                  Error Details:
                </Typography>
                <Box
                  sx={{
                    maxHeight: 300,
                    overflow: 'auto',
                    border: '1px solid #dedede',
                    borderRadius: 1,
                    p: 2,
                    backgroundColor: '#f5f5f5',
                  }}
                >
                  {result.errors.map((err) => (
                    <Typography
                      key={err.id}
                      variant="body2"
                      sx={{ fontFamily: 'monospace', mb: 1 }}
                    >
                      {err.barcode}: {err.error}
                    </Typography>
                  ))}
                </Box>
              </Box>
            )}
          </Box>
        )}
      </CardContent>
    </Card>
  )
}

export default ChipExportTool
